import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { ProviderAccount } from '@prisma/client'
import { Button, Select, SelectProps } from 'antd'
import React, { ReactNode, useMemo } from 'react'
import { useUserContext } from '~/core/context'
import { Api } from '~/core/trpc'
import { FeatureProviderAccountShared } from '~/features/providerAccount/client/shared'
import { ProviderClient } from '~/plugins/data-connector/providers/abstract/provider.client'
import { useAuthentication } from '../../hooks/useAuthentication'
import { ModalAuthentication } from '../ModalAuthentication'

type Props = {
  provider: ProviderClient
  providerAccount?: ProviderAccount
  providerAccountId?: string
  scopes?: string[]
  onChange?: (providerAccount: ProviderAccount) => void
  onClear?: () => void
  propsSelect?: Partial<SelectProps>
  renderEmpty?: (options: { onClick: () => void }) => ReactNode
}

export const SelectAccount: React.FC<Props> = ({
  provider,
  providerAccountId,
  providerAccount: providerAccountSelected,
  scopes,
  onChange,
  onClear,
  propsSelect = {},
  renderEmpty,
}) => {
  const { session } = useUserContext()

  const accountsQuery = Api.providerAccount.findMany.useQuery(
    {
      where: { providerKey: provider?.key, userId: session.user.id },
    },
    { enabled: !!provider, initialData: [] },
  )

  const providerAccounts = accountsQuery.data

  const providerAccount = useMemo(() => {
    const id = providerAccountSelected?.id ?? providerAccountId
    const accountFound = accountsQuery.data.find(
      candidate => candidate.id === id,
    )

    return accountFound
  }, [accountsQuery.data, providerAccountSelected?.id, providerAccountId])

  const getExtraAuthParams = () => {
    if (!provider) {
      return {}
    }

    switch (provider.key) {
      case 'nylas':
        return {
          provider: 'imap',
          prompt: undefined,
        }
      default:
        return {}
    }
  }

  const authentication = useAuthentication({
    provider,
    providerAccount,
    providerAccounts,
    scopes,
    extraAuthParams: getExtraAuthParams(),
    onSubmit(providerAccount) {
      onChange(providerAccount)
      accountsQuery.refetch()
    },
  })

  const authenticationNew = useAuthentication({
    provider,
    scopes,
    extraAuthParams: getExtraAuthParams(),
    onSubmit(providerAccount) {
      onChange(providerAccount)
      accountsQuery.refetch()
    },
  })

  const handleChange = (accountId: string) => {
    const account = accountsQuery.data.find(account => account.id === accountId)

    if (account) {
      onChange(account)
    }
  }

  const handleSelect = (accountId: string) => {
    const account = accountsQuery.data.find(account => account.id === accountId)

    if (account?.status === 'CORRUPTED') {
      authentication.start()
    }
  }

  if (!accountsQuery.isFetched) {
    return (
      <>
        <LoadingOutlined />
      </>
    )
  }

  const isEmpty = accountsQuery.data.length === 0

  const options = accountsQuery.data.map(account => ({
    label: <FeatureProviderAccountShared.Label providerAccount={account} />,
    value: account.id,
  }))

  return (
    <>
      {isEmpty ? (
        <>
          {renderEmpty ? (
            renderEmpty({ onClick: () => authenticationNew.start() })
          ) : (
            <Button
              icon={<PlusOutlined />}
              onClick={() => authenticationNew.start()}
            >
              Connect an account
            </Button>
          )}
        </>
      ) : (
        <>
          <Select
            placeholder="Select an account"
            {...propsSelect}
            loading={accountsQuery.isLoading}
            value={providerAccount?.id}
            onChange={handleChange}
            onSelect={handleSelect}
            onClear={onClear}
            allowClear={!!onClear}
            options={[
              ...options,
              {
                label: (
                  <Button
                    icon={<PlusOutlined />}
                    block
                    onClick={() => authentication.start()}
                  >
                    Connect account
                  </Button>
                ),
                value: 'create',
                disabled: true,
              },
            ]}
          />
        </>
      )}

      <ModalAuthentication {...authenticationNew.propsModal} />
      <ModalAuthentication {...authentication.propsModal} />
    </>
  )
}
